import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { COMPLIANCE_LABEL, fmtDate } from "@/lib/format";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { TrendingUp, Percent, Timer, AlertOctagon, Landmark, Gavel, Users, FileDown, CalendarClock } from "lucide-react";

const MIX_COLORS = { up_to_date: "#10b981", due: "#f59e0b", overdue: "#ef4444", never_visited: "#a1a1aa" };

const Kpi = ({ label, value, sub, icon: Icon, testid, accent = "text-primary" }) => (
  <div data-testid={testid} className="surface p-5 flex flex-col justify-between">
    <div className="flex items-start justify-between">
      <p className="label-tiny">{label}</p>
      <Icon size={16} className={accent} />
    </div>
    <div className="mt-5">
      <p className="font-display text-3xl font-bold tracking-tighter">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

const Card = ({ title, children, testid, right }) => (
  <div className="surface p-6" data-testid={testid}>
    <div className="flex items-center justify-between gap-3">
      <p className="label-tiny">{title}</p>
      {right}
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

const tip = {
  contentStyle: { borderRadius: 10, border: "1px solid hsl(var(--border))", fontSize: 12 },
};

export default function Analytics() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setD(data)).catch(() => setD(false));
  }, []);

  if (d === null) return <div className="p-8 text-sm text-muted-foreground">Building analytics…</div>;
  if (d === false) return <div className="p-8 text-sm text-destructive" data-testid="analytics-error">Could not load analytics.</div>;

  const k = d.kpis;
  const mix = d.compliance_mix.filter((m) => m.value > 0);

  return (
    <div className="p-5 md:p-10 max-w-[1500px] rise" data-testid="analytics-page">
      <p className="label-tiny">Portfolio intelligence</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">Analytics</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Visit compliance is measured against your SLA of one geotagged capture every{" "}
        <span className="font-semibold text-foreground">{d.sla.visit_interval_days} days</span>, overdue
        past {d.sla.overdue_after_days} days and escalated past {d.sla.escalation_after_days} days.
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Kpi testid="kpi-compliance" label="Compliance" value={`${k.compliance_pct}%`}
          sub={`${k.total_parcels} assets monitored`} icon={Percent} accent="text-emerald-500" />
        <Kpi testid="kpi-avg-gap" label="Avg visit gap"
          value={k.avg_days_since_visit === null ? "—" : `${k.avg_days_since_visit}d`}
          sub="since last capture" icon={Timer} />
        <Kpi testid="kpi-attention" label="Need a visit" value={k.attention}
          sub={`${k.escalated} past escalation`} icon={AlertOctagon} accent="text-red-500" />
        <Kpi testid="kpi-visits" label="Captures" value={k.visits_total} sub="all time" icon={TrendingUp} />
        <Kpi testid="kpi-mortgage" label="Mortgaged" value={d.risk.active_mortgages}
          sub={d.risk.mortgage_amount ? `₹${(d.risk.mortgage_amount / 10000000).toFixed(2)} Cr outstanding` : "no active loans"} icon={Landmark}
          accent="text-cyan-500" />
        <Kpi testid="kpi-legal" label="Open cases" value={d.risk.open_cases}
          sub={`${d.risk.hearings_30} hearing(s) in 30d`} icon={Gavel} accent="text-red-500" />
      </div>

      <Link to="/renewals" data-testid="renewals-shortcut"
        className={`mt-4 flex items-center justify-between gap-4 surface p-5 transition-colors duration-200 hover:border-primary/50 ${
          d.risk.expiring_docs_30 ? "border-amber-300 bg-amber-50/60" : ""}`}>
        <div className="flex items-start gap-3">
          <CalendarClock size={19} className={d.risk.expiring_docs_30 ? "text-amber-600 mt-0.5" : "text-primary mt-0.5"} />
          <div>
            <p className="text-sm font-semibold">
              {d.risk.expiring_docs_30
                ? `${d.risk.expiring_docs_30} document(s) lapse within 30 days`
                : "No document renewals due in the next 30 days"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Leases, tax receipts, ECs, insurance and NOCs with a validity date on file
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold text-primary whitespace-nowrap">View renewals</span>
      </Link>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 surface p-5">
        <div>
          <p className="text-sm font-semibold">Board snapshot</p>
          <p className="mt-1 text-xs text-muted-foreground">
            A one-page PDF of this dashboard — KPIs, compliance mix, districts, executive
            performance and assets needing attention. Ready to send investors.
          </p>
        </div>
        <a href={`${API}/reports/board-snapshot.pdf?auth=${localStorage.getItem("token")}`}
          target="_blank" rel="noreferrer" data-testid="board-snapshot-link"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-transform duration-150 active:scale-[0.98]">
          <FileDown size={16} /> Download PDF
        </a>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card title="Field capture trend (12 months)" testid="chart-visit-trend">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.visit_trend} margin={{ left: -20, right: 8, top: 4 }}>
                  <defs>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0f766e" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="month" tickFormatter={(m) => m.slice(5)} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip {...tip} />
                  <Area type="monotone" dataKey="visits" stroke="#0f766e" fill="url(#gv)" strokeWidth={2} />
                  <Area type="monotone" dataKey="flagged" stroke="#ef4444" fill="none" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Teal: captures recorded · Red: captures with a flagged observation
            </p>
          </Card>
        </div>

        <Card title="Compliance mix" testid="chart-compliance-mix">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={mix} dataKey="value" nameKey="key" innerRadius={48} outerRadius={72} paddingAngle={3}>
                  {mix.map((m) => <Cell key={m.key} fill={MIX_COLORS[m.key]} />)}
                </Pie>
                <Tooltip {...tip} formatter={(v, n) => [v, COMPLIANCE_LABEL[n]]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {d.compliance_mix.map((m) => (
              <div key={m.key} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: MIX_COLORS[m.key] }} />
                  {COMPLIANCE_LABEL[m.key]}
                </span>
                <span className="font-mono">{m.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Extent by category (acres)" testid="chart-category-mix">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.category_mix} margin={{ left: -20, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip {...tip} />
                <Bar dataKey="acres" fill="#0f766e" radius={[6, 6, 0, 0]} maxBarSize={54} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Assets by district" testid="chart-district-mix">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.district_mix} layout="vertical" margin={{ left: 24, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                <Tooltip {...tip} />
                <Bar dataKey="count" fill="#0891b2" radius={[0, 6, 6, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="mt-4 surface p-6" data-testid="exec-performance">
        <p className="label-tiny flex items-center gap-2"><Users size={14} className="text-primary" /> Executive performance</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Executive", "Assigned", "On time", "Visits 30d", "Visits 90d", "Flags", "Last activity"].map((h) => (
                  <th key={h} className="px-3 py-3 label-tiny whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {d.executives.map((e) => (
                <tr key={e.id} data-testid={`exec-row-${e.id}`} className="hover:bg-muted/50 transition-colors duration-150">
                  <td className="px-3 py-3 font-semibold whitespace-nowrap">{e.name}</td>
                  <td className="px-3 py-3 font-mono text-xs">{e.assigned}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${e.on_time_pct}%` }} />
                      </div>
                      <span className="font-mono text-xs">{e.on_time_pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">{e.visits_30}</td>
                  <td className="px-3 py-3 font-mono text-xs">{e.visits_90}</td>
                  <td className="px-3 py-3 font-mono text-xs">{e.flagged}</td>
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(e.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {d.executives.length === 0 && (
            <p className="py-8 text-sm text-muted-foreground text-center">No executives added yet.</p>
          )}
        </div>
      </div>

      <div className="mt-4 surface p-6" data-testid="worst-offenders">
        <p className="label-tiny flex items-center gap-2">
          <AlertOctagon size={14} className="text-red-500" /> Longest without a site visit
        </p>
        {d.worst_offenders.length === 0 ? (
          <p className="mt-5 text-sm text-muted-foreground">Every asset is within SLA. Nothing to escalate.</p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {d.worst_offenders.map((o) => (
              <Link key={o.parcel_id} to={`/parcels/${o.parcel_id}`} data-testid={`offender-${o.parcel_id}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3 group">
                <div className="min-w-0">
                  <p className="text-sm font-semibold group-hover:text-primary transition-colors duration-200">
                    {o.name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {o.district} · {o.executive} · last visit {fmtDate(o.last_visit_at)}
                  </p>
                </div>
                <span className={`text-xs font-mono px-2.5 py-1 rounded-md border ${
                  o.escalated ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                  {o.days_since === null ? "Never visited" : `${o.days_since} days`}
                  {o.escalated ? " · escalated" : ""}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
